import { useEffect, useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CreditCard, FileText, Crown, Loader2, AlertCircle, Download, CheckCircle } from 'lucide-react';
import { useBillingStore } from '@/stores/billingStore';
import { getStripe } from '@/lib/stripe';

type Tab = 'subscription' | 'payment' | 'invoices';

interface ManageSlideOverProps {
  open: boolean;
  onClose: () => void;
  initialTab?: Tab;
}

export function ManageSlideOver({ open, onClose, initialTab = 'subscription' }: ManageSlideOverProps) {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  // Handle open/close transitions
  useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
    }
  }, [open]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setVisible(false);
      onClose();
    }, 250);
  };

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [visible]);

  // Lock body scroll when open
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [visible]);

  const subscriptionStatus = useBillingStore((s) => s.subscriptionStatus);
  const isPastDue = subscriptionStatus === 'past_due';

  // Reset tab when opening
  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  if (!visible) return null;

  const tabs: { id: Tab; label: string; icon: typeof Crown; alert?: boolean }[] = [
    { id: 'subscription', label: 'Subscription', icon: Crown },
    { id: 'payment', label: 'Payment', icon: CreditCard, alert: isPastDue },
    { id: 'invoices', label: 'Invoices', icon: FileText },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 transition-opacity duration-250 ${closing ? 'opacity-0' : 'opacity-100'}`}
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={handleClose}
      />

      {/* Panel */}
      <div
        className={`relative w-full max-w-[480px] bg-card border-l border-border shadow-2xl flex flex-col transition-transform duration-250 ease-out ${closing ? 'translate-x-full' : 'animate-slide-in-right'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Manage Subscription</h2>
          <button
            onClick={handleClose}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-5">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
              {tab.alert && <span className="h-1.5 w-1.5 rounded-full bg-red-400" />}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'subscription' && <SubscriptionTab onClose={handleClose} />}
          {activeTab === 'payment' && <PaymentTab />}
          {activeTab === 'invoices' && <InvoicesTab />}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Subscription Tab
// =============================================================================

function SubscriptionTab({ onClose }: { onClose: () => void }) {
  const {
    plan, subscriptionStatus, seats, currentPeriodEnd, trialEnd,
    cancelAtPeriodEnd, cancelSubscription, reactivateSubscription,
    paymentMethod, fetchPaymentMethod,
  } = useBillingStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    fetchPaymentMethod();
  }, [fetchPaymentMethod]);

  const isTrialing = subscriptionStatus === 'trialing';
  const daysLeft = trialEnd
    ? Math.max(0, Math.ceil((new Date(trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const handleCancel = async () => {
    setLoading(true);
    setError(null);
    try {
      await cancelSubscription();
      onClose();
    } catch {
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    setError(null);
    try {
      await reactivateSubscription();
      onClose();
    } catch {
      setError('Failed to reactivate subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Plan info */}
      <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-primary" />
          <span className="font-medium text-foreground">
            {plan === 'pro' ? 'Pro Plan' : 'Free Plan'}
          </span>
          {isTrialing && daysLeft !== null && (
            <span className="rounded-full bg-primary/10 border border-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
              Trial — {daysLeft} days left
            </span>
          )}
          {cancelAtPeriodEnd && (
            <span className="rounded-full bg-yellow-500/10 border border-yellow-500/20 px-2 py-0.5 text-xs font-medium text-yellow-400">
              Cancelling
            </span>
          )}
        </div>

        <div className="text-sm text-muted-foreground space-y-1">
          <p>$50/mo base{seats > 0 ? ` + ${seats} additional seat${seats === 1 ? '' : 's'} ($${seats}/mo)` : ''}</p>
          {currentPeriodEnd && (
            <p>
              {cancelAtPeriodEnd ? 'Subscription ends' : 'Next billing'}:{' '}
              {new Date(currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
      </div>

      {/* Payment method summary */}
      {paymentMethod && (
        <div className="rounded-lg border border-border bg-secondary/30 p-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-foreground">
              {paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)} ending in {paymentMethod.last4}
            </span>
            <span className="text-sm text-muted-foreground">
              expires {paymentMethod.expMonth}/{paymentMethod.expYear}
            </span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      {/* Cancel / Reactivate */}
      {plan === 'pro' && !cancelAtPeriodEnd && !confirmCancel && (
        <button
          onClick={() => setConfirmCancel(true)}
          disabled={loading}
          className="w-full rounded-md border border-red-500/30 px-4 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
        >
          Cancel Subscription
        </button>
      )}

      {confirmCancel && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 space-y-3">
          <p className="text-sm text-foreground">
            Are you sure you want to cancel? You'll keep access until{' '}
            {currentPeriodEnd
              ? new Date(currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
              : 'the end of your billing period'}.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Yes, Cancel'}
            </button>
            <button
              onClick={() => setConfirmCancel(false)}
              disabled={loading}
              className="flex-1 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
            >
              Keep Subscription
            </button>
          </div>
        </div>
      )}

      {cancelAtPeriodEnd && (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4 space-y-3">
          <p className="text-sm text-foreground">
            Your subscription is set to cancel at the end of your billing period. You can reactivate to keep your plan.
          </p>
          <button
            onClick={handleReactivate}
            disabled={loading}
            className="w-full rounded-md bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Reactivate Subscription'}
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Payment Tab
// =============================================================================

function PaymentTab() {
  const {
    paymentMethod, fetchPaymentMethod, createSetupIntent, updateDefaultPaymentMethod,
  } = useBillingStore();

  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchPaymentMethod();
  }, [fetchPaymentMethod]);

  const handleStartUpdate = async () => {
    setLoading(true);
    setError(null);
    try {
      const secret = await createSetupIntent();
      if (!secret) throw new Error('Failed to create setup intent');
      setSetupSecret(secret);
    } catch {
      setError('Failed to start payment update. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentMethodId: string) => {
    setError(null);
    try {
      await updateDefaultPaymentMethod(paymentMethodId);
      setSetupSecret(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('Failed to update default payment method. Please try again.');
    }
  };

  return (
    <div className="space-y-5">
      {/* Current card */}
      <div className="rounded-lg border border-border bg-secondary/30 p-4">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Current Payment Method</p>
        {paymentMethod ? (
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-foreground">
              {paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)} ending in {paymentMethod.last4}
            </span>
            <span className="text-sm text-muted-foreground">
              expires {paymentMethod.expMonth}/{paymentMethod.expYear}
            </span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No payment method on file</p>
        )}
      </div>

      {success && (
        <div className="flex items-center gap-2 text-sm text-green-400">
          <CheckCircle className="h-3.5 w-3.5" />
          Payment method updated successfully.
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      {/* Update form or button */}
      {setupSecret ? (
        <div className="rounded-lg border border-border p-4">
          <Elements stripe={getStripe()} options={{ clientSecret: setupSecret, appearance: { theme: 'night', variables: { colorPrimary: '#6366f1' } } }}>
            <SetupForm onSuccess={handlePaymentSuccess} onCancel={() => setSetupSecret(null)} />
          </Elements>
        </div>
      ) : (
        <button
          onClick={handleStartUpdate}
          disabled={loading}
          className="w-full rounded-md border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...
            </span>
          ) : (
            'Update Payment Method'
          )}
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Setup Form (runs inside Elements provider)
// =============================================================================

function SetupForm({ onSuccess, onCancel }: { onSuccess: (paymentMethodId: string) => void; onCancel: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message ?? 'Payment setup failed.');
      setLoading(false);
      return;
    }

    const setupIntent = result.setupIntent;
    if (setupIntent?.payment_method && typeof setupIntent.payment_method === 'string') {
      onSuccess(setupIntent.payment_method);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading || !stripe}
          className="flex-1 rounded-md bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Save Card'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="rounded-md border border-border px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// Invoices Tab
// =============================================================================

function InvoicesTab() {
  const { invoices, fetchInvoices } = useBillingStore();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    setFetchError(false);
    fetchInvoices()
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false));
  }, [fetchInvoices]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Failed to load invoices</p>
        <button
          onClick={() => { setLoading(true); setFetchError(false); fetchInvoices().catch(() => setFetchError(true)).finally(() => setLoading(false)); }}
          className="mt-2 text-xs text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No invoices yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {invoices.map((inv) => (
        <div key={inv.id} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 p-3">
          <div className="space-y-0.5">
            <p className="text-sm text-foreground">
              {new Date(inv.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                ${(inv.amount / 100).toFixed(2)}
              </span>
              <StatusBadge status={inv.status} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            {inv.hostedUrl && (
              <a
                href={inv.hostedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                View
              </a>
            )}
            {inv.pdfUrl && (
              <a
                href={inv.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                title="Download PDF"
              >
                <Download className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    paid: 'bg-green-500/10 text-green-400 border-green-500/20',
    open: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    draft: 'bg-secondary text-muted-foreground border-border',
    void: 'bg-secondary text-muted-foreground border-border',
    uncollectible: 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  return (
    <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${styles[status] ?? styles.draft}`}>
      {status}
    </span>
  );
}
